import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import { useStore } from "@nanostores/react";
import { ArrowsLeftRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowsLeftRight";
import { FocusTrap } from "focus-trap-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { analyticsEvent } from "@up/analytics";
import { useCart, CART_STATUS } from "@up/checkout";
import {
  FILTER_NAMES,
  filterCohorts,
  generateDurationFilter,
  generateStartDateFilter,
  handleCohortFilterChange,
  logError,
  resetCohortFilters,
  useTheme,
} from "@up/data";
import { LoadingModal, Ticker, Toast, ToastTitle } from "@up/ui";

import { Portal } from "./cohort-switch-portal";
import { cohortToSwitch } from "./cohort-switch-store";
import { Filters } from "./filters";

import type { FilterType, TCatalogueItem, TInventoryItem } from "@up/data";
import type { ChangeEvent } from "react";
import { HTTPError } from "ky";

const GALAXY_URL = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"];

const generateFilters = function (
  inventoryItems: TInventoryItem[],
  previousFilters?: FilterType[],
) {
  const enabledFilters = {
    startDate: true,
    duration: true,
    course: false,
  };

  const previousF = previousFilters ?? [];

  const startDateFilter = previousF.find(
    (f) => f.name === FILTER_NAMES.START_DATE,
  );

  const durationFilter = previousF.find(
    (f) => f.name === FILTER_NAMES.DURATION,
  );

  return [
    generateStartDateFilter({
      enabledFilters,
      inventoryItems,
      previousFilter: startDateFilter,
    }),
    generateDurationFilter({
      enabledFilters,
      inventoryItems,
      previousFilter: durationFilter,
    }),
  ];
};

type Props = {
  successCallback: () => void;
  ownerContactId?: string;
};

export const CohortSwitchDrawer = function ({
  successCallback,
  ownerContactId,
}: Props) {
  const auth = useAuthStore();
  const theme = useTheme();
  const cart = useCart();
  const $cohortToSwitch = useStore(cohortToSwitch);

  const [selectedCatalogueItem, setSelectedCatalogueItem] = useState<
    TCatalogueItem | undefined
  >(undefined);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<
    TInventoryItem | undefined
  >(undefined);
  const [catalogueItems, setCatalogueItems] = useState<TCatalogueItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const isDesktopScreen = useMediaQuery("(min-width: 640px)");
  const isOpen = Boolean($cohortToSwitch) || saveSuccess;

  const [filters, setFilters] = useState(() => {
    return generateFilters([]);
  });
  const handleFilterToggle = function (filterName: string, optionName: string) {
    setFilters(handleCohortFilterChange(filters, filterName, optionName));
  };

  useEffect(() => {
    const go = async function () {
      if (!cart.inventory?.length) {
        return;
      }

      const foundCI = cart.inventory.find((ci) => {
        return ci._id === $cohortToSwitch?.catalogueItem._id;
      });
      setCatalogueItems(
        cart.inventory.filter((i) => {
          return i.type === $cohortToSwitch?.catalogueItem.type;
        }),
      );

      const defaultCI = foundCI ? foundCI : cart.inventory[0];
      setFilters(generateFilters(defaultCI.cohorts, filters));
      setSelectedCatalogueItem(defaultCI);
      setSelectedInventoryItem(
        defaultCI?.cohorts.find(
          (cohort) => cohort._id !== $cohortToSwitch?._id,
        ),
      );
    };

    go();
  }, [$cohortToSwitch, cart.inventory, cart.data?.lineItems]);
  // do not add `filters`!

  const close = () => {
    setSaveSuccess(false);
    cohortToSwitch.set(undefined);
  };

  const handleCatalogueItemChange = function (
    e: ChangeEvent<HTMLSelectElement>,
  ) {
    const ci = catalogueItems.find((item) => {
      return item._id === e.target.value;
    });
    if (ci) {
      setSelectedCatalogueItem(ci);
      setFilters(generateFilters(ci.cohorts, filters));
      setSelectedInventoryItem(
        ci.cohorts.find((cohort) => cohort._id !== $cohortToSwitch?._id),
      );
    }
  };

  const handleInventoryItemChange = function (
    e: ChangeEvent<HTMLSelectElement>,
  ) {
    const ii = selectedCatalogueItem?.cohorts.find((item) => {
      return item._id === e.target.value;
    });
    if (ii) {
      setSelectedInventoryItem(ii);
    }
  };

  const handleSubmit = async function () {
    if (!auth.currentUser || !$cohortToSwitch || !selectedInventoryItem) {
      return;
    }
    setIsSaving(true);
    // New up-enrollments stack: student self-service switch lives under
    // /v2/enrollments/self/*. The acting student's contactId is taken from the
    // auth token server-side (not sent here); ownerContactId is only forwarded
    // when a guardian is switching on behalf of a related minor.
    const URL = `${GALAXY_URL}/v2/enrollments/self/enrollments/switch`;

    try {
      await auth.client.post(URL, {
        json: {
          prevInventoryId: $cohortToSwitch._id,
          nextInventoryId: selectedInventoryItem._id,
          ...(ownerContactId ? { ownerContactId } : {}),
        },
        timeout: 30000,
      });
      setSaveSuccess(true);
      analyticsEvent({
        event: "postEnrollmentCohortSwitch",
        prevInventoryItem: $cohortToSwitch._id,
        newInventoryItem: selectedInventoryItem._id,
      });
      setTimeout(() => {
        close();
        successCallback();
      }, 3000);
    } catch (err) {
      logError({ action: "COHORT_SWITCH", error: err });
      if (err instanceof HTTPError && err.response?.status === 400) {
        const msg = typeof err.data === "string" ? err.data : undefined;
        setSaveError(
          msg ?? "We were unable to process your cohort switch as requested.",
        );
      } else {
        setSaveError(
          "We were unable to process your cohort switch as requested.",
        );
      }
    }

    setIsSaving(false);
  };

  const foundCI = cart?.inventory?.find((ci) => {
    return ci._id === $cohortToSwitch?.catalogueItem._id;
  });

  if (!isOpen) {
    return null;
  }

  const filteredCohorts = filterCohorts(
    selectedCatalogueItem?.cohorts ?? [],
    filters,
  ).filter((cohort) => {
    return cohort._id !== $cohortToSwitch?._id;
  });

  return (
    <Portal>
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          initialFocus: "#cohort-switch-heading",
        }}
      >
        <div
          aria-hidden={!isOpen}
          aria-labelledby="cohort-switch-heading"
          data-disable-document-scroll={isOpen}
          role="dialog"
          tabIndex={-1}
        >
          {isOpen ? (
            <div
              className="fixed inset-0 bg-black/30 z-40 cursor-pointer animation-fade-in"
              onClick={close}
            />
          ) : null}
          <motion.div
            animate={isOpen ? "open" : "closed"}
            className={`${
              isDesktopScreen ? "w-96" : "w-full"
            } fixed z-50 top-0 right-0 bottom-0 bg-white overflow-auto overscroll-contain`}
            initial="closed"
            style={{
              transformOrigin: "top center",
              boxShadow:
                isDesktopScreen && isOpen
                  ? "0px 30px 50px 0px rgba(0, 0, 0, 0.20), 0px 15px 20px 0px rgba(0, 0, 0, 0.10), 0px 0px 4px 0px rgba(0, 0, 0, 0.07)"
                  : "",
            }}
            transition={{
              type: "spring",
              bounce: 0.0,
              duration: 0.4,
            }}
            variants={{
              open: isDesktopScreen ? { x: "0", y: 0 } : { y: "0", x: 0 },
              closed: isDesktopScreen
                ? { x: "100%", y: 0 }
                : { x: 0, y: "100%" },
            }}
          >
            {cart.status === CART_STATUS.LOADING ? (
              <LoadingModal local />
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 bg-white/95 sticky top-0 z-50 shrink-0">
                  <p
                    className="flex items-center font-bold text-xl"
                    id="cohort-switch-heading"
                    tabIndex={0}
                  >
                    <ArrowsLeftRightIcon
                      aria-hidden
                      size={32}
                      className="mr-2"
                    />{" "}
                    Switch Course
                  </p>
                  <button className="underline" onClick={close}>
                    close
                  </button>
                </div>
                {saveError ? (
                  <div className="relative grow pb-6">
                    <div className="flex flex-col gap-6 px-4 pt-4">
                      <Toast variant="error">
                        <ToastTitle>{saveError}</ToastTitle>
                        Please{" "}
                        <button
                          className="underline"
                          onClick={() => {
                            setSaveError(null);
                            setIsSaving(false);
                          }}
                        >
                          try again
                        </button>{" "}
                        or{" "}
                        <a
                          className="underline"
                          href={`mailto:${theme.supportEmail}`}
                        >
                          email support
                        </a>
                        .
                      </Toast>
                    </div>
                  </div>
                ) : null}
                {saveSuccess ? (
                  <div className="relative grow pb-6">
                    <div className="flex flex-col gap-6 px-4 pt-4">
                      <Toast variant="success">
                        <ToastTitle>
                          Your course has been successfully switched
                        </ToastTitle>
                      </Toast>
                      <div>
                        <strong>New course details:</strong>
                        <br />
                        {selectedCatalogueItem?.name}
                        <br />
                        {selectedInventoryItem?.name}
                      </div>
                      <div>
                        <button
                          className={theme.buttonPrimaryClass}
                          onClick={close}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                {!saveSuccess && !saveError ? (
                  <div className="relative grow pb-6">
                    {isSaving ? <LoadingModal local /> : null}
                    {$cohortToSwitch ? (
                      <div className="flex flex-col gap-6 px-4 pt-4">
                        <div>
                          <h2 className={theme.heading3Class}>
                            Current Enrollment:
                          </h2>
                          <div>{$cohortToSwitch.catalogueItem.name}</div>
                          <div>
                            {$cohortToSwitch.formattedStartDate} —
                            {$cohortToSwitch.formattedEndDate}
                          </div>
                        </div>
                        <div>
                          <h2 className={theme.heading3Class}>Switch to:</h2>
                          {!foundCI ? (
                            <div className="pt-2 mb-4">
                              <Toast variant="warning" showIcon={false}>
                                We don't have other cohorts available for your
                                current course. Perhaps you'd like to try
                                something else?
                              </Toast>
                            </div>
                          ) : null}
                          <div>
                            <label htmlFor="switch-course-catalogue-item">
                              Course
                            </label>
                            <select
                              className="w-full mb-2 bg-white border border-gray-400 rounded py-1 px-3 shadow-sm"
                              disabled={isSaving}
                              id="switch-course-catalogue-item"
                              name="program"
                              onChange={handleCatalogueItemChange}
                              value={selectedCatalogueItem?._id ?? "0"}
                            >
                              {catalogueItems?.map((p) => {
                                return (
                                  <option key={p._id} value={p._id}>
                                    {p.name}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {filters.map((f) => f.enabled).length ? (
                            <div className="pt-3">
                              <div
                                className="pt-3 border-t border-gray-400 border-dashed"
                                aria-hidden={true}
                              />
                              <Filters
                                filters={filters}
                                handleFilterToggle={handleFilterToggle}
                              />
                            </div>
                          ) : null}

                          {filteredCohorts.length ? (
                            <div>
                              <label htmlFor="switch-course-inventory-item">
                                <Ticker
                                  value={`Date (${filteredCohorts.length.toString()} available)`}
                                />
                              </label>
                              <select
                                className="w-full mb-2 bg-white border border-gray-400 rounded py-1 px-3 shadow-sm"
                                disabled={isSaving}
                                id="switch-course-inventory-item"
                                name="program"
                                onChange={handleInventoryItemChange}
                                value={selectedInventoryItem?._id ?? "0"}
                              >
                                {filteredCohorts?.map((p) => {
                                  return (
                                    <option key={p._id} value={p._id}>
                                      {p.name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          ) : (
                            <div className="animation-fade-in">
                              <div className="pt-2 mb-2">
                                No courses match these filters
                              </div>
                              <button
                                className={`${theme.buttonPrimaryClass} w-full`}
                                onClick={() => {
                                  setFilters(resetCohortFilters(filters));
                                }}
                              >
                                Reset Filters
                              </button>
                            </div>
                          )}
                          {filteredCohorts.length ? (
                            <div className="pt-3">
                              <button
                                className={theme.buttonPrimaryClass}
                                data-location={{
                                  location: "cohort-switch-drawer",
                                }}
                                data-testid="add-to-cart-button"
                                disabled={isSaving}
                                onClick={handleSubmit}
                              >
                                Switch Course
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </motion.div>
        </div>
      </FocusTrap>
    </Portal>
  );
};
